import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Wand2, ArrowRight, Globe, LayoutDashboard, ShoppingCart, FileText, Smartphone, AppWindow } from "lucide-react";

const PLATFORMS = [
  { id: "website", name: "Website", icon: Globe, desc: "Landing pages, blogs, marketing" },
  { id: "webapp", name: "Web App", icon: AppWindow, desc: "Dashboards, SaaS, tools" },
  { id: "mobile", name: "Mobile App", icon: Smartphone, desc: "Mobile-first PWA app" },
];

const IDEAS = [
  { icon: Globe, text: "A landing page for a coffee shop with menu and hours" },
  { icon: LayoutDashboard, text: "An analytics dashboard with KPI cards and charts" },
  { icon: ShoppingCart, text: "A product storefront with a cart and checkout" },
  { icon: FileText, text: "A personal blog with a featured post and cards" },
];

export default function NewProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = useState(location.state?.prompt || "");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("webapp");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!prompt.trim()) return toast.error("Describe what you want to build");
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { prompt, name, platform });
      navigate(`/workspace/${data.id}`, { state: { autoRun: prompt } });
    } catch (e) {
      toast.error("Could not create project");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="new-project">
      <div className="text-center">
        <div className="inline-flex h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center mb-4 dc-glow">
          <Wand2 className="h-6 w-6 text-slate-950" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold">What should we build?</h1>
        <p className="text-slate-400 mt-2">Pick a type, describe your app, and the agents handle the rest.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {PLATFORMS.map((p) => (
          <button key={p.id} data-testid={`platform-${p.id}`} onClick={() => setPlatform(p.id)}
            className={`rounded-2xl border p-4 text-left transition-colors ${platform === p.id ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-800 bg-slate-900/40 hover:border-slate-700"}`}>
            <p.icon className={`h-5 w-5 mb-2 ${platform === p.id ? "text-emerald-400" : "text-slate-400"}`} />
            <div className="font-head font-semibold text-sm">{p.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{p.desc}</div>
          </button>
        ))}
      </div>

      <div className="dc-glass border border-slate-800 rounded-2xl p-5 space-y-4">
        <input data-testid="new-project-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Project name (optional)"
          className="w-full h-11 px-4 rounded-xl bg-slate-900/70 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none" />
        <textarea data-testid="new-project-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
          placeholder="Build a landing page for a coffee shop with a hero, menu section and contact form..."
          className="w-full resize-none px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none" />
        <button data-testid="new-project-submit" disabled={busy} onClick={create}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
          {busy ? "Creating..." : <>Create & build <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>

      <div>
        <p className="text-sm text-slate-500 mb-3">Need inspiration?</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {IDEAS.map((idea, i) => (
            <button key={i} data-testid={`idea-${i}`} onClick={() => setPrompt(idea.text)}
              className="text-left flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-emerald-500/40 transition-colors">
              <idea.icon className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-300">{idea.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
